import React, { useState } from 'react';
import Box from '../../box';
import Typography from '../../typography';
import Grid from '../../grid';
import { TimeRange } from './availability_step_context';
import Button from '../../button';
import ClearIcon from '@mui/icons-material/ClearAll';
import AddIcon from '@mui/icons-material/AddBox';
import Interval from './time_interval';
import { uuid } from '../../../utils';

export interface TimeRangeWithId extends TimeRange {
  id: string
}

interface AvailabilityDayProps {
  day: string
  onSetTimeRanges: (timeRange: TimeRange[]) => void
  timeranges: TimeRange[]
}

export const DayAvailabilitySelector: React.FC<AvailabilityDayProps> = ({
  day, onSetTimeRanges, timeranges: parentTimeRange
}) => {
  const [timerangeWithIds, setTimeRangesWithId] = useState<TimeRangeWithId[]>(parentTimeRange.map((timeRange) => ({
    ...timeRange,
    id: uuid()
  })));

  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);

  const checkOverlappingTimeRanges = (timeRanges: TimeRange[]): boolean => {
    const sortedTimeRanges = timeRanges.sort((a, b) => {
      if (a.startTime.hour < b.startTime.hour) return -1;
      if (a.startTime.hour > b.startTime.hour) return 1;
      if (a.startTime.minute < b.startTime.minute) return -1;
      if (a.startTime.minute > b.startTime.minute) return 1;
      return 0;
    });
    for (let i = 0; i < sortedTimeRanges.length - 1; i++) {
      const timeRange = sortedTimeRanges[i];
      const nextTimeRange = sortedTimeRanges[i + 1];
      if (timeRange.endTime.hour > nextTimeRange.startTime.hour) return true;
      if (timeRange.endTime.hour === nextTimeRange.startTime.hour && timeRange.endTime.minute > nextTimeRange.startTime.minute) return true;
    }
    return false;
  };

  const syncTimeRanges = (timeRanges: TimeRangeWithId[]): void => {
    setTimeRangesWithId(timeRanges);
    const newTimeRangesWithoutIds = timeRanges.map((timeRange) => ({
      startTime: timeRange.startTime,
      endTime: timeRange.endTime
    }));
    onSetTimeRanges(newTimeRangesWithoutIds);
  };

  const handleClearAvailability = (): void => {
    syncTimeRanges([]);
  };

  const handleSetTimeRangeOnInterval = (index: number, internvalTimeRange?: TimeRangeWithId): void => {
    if (!internvalTimeRange && timerangeWithIds.length === 1) {
      // if there is only one time range and it is being cleared, clear all availability
      handleClearAvailability();
      return;
    }
    const newTimeRanges = [...timerangeWithIds]
    if (internvalTimeRange) {
      console.log('internvalTimeRange', internvalTimeRange)
      newTimeRanges[index] = internvalTimeRange;
    } else {
      console.log('internvalTimeRange', internvalTimeRange)
      newTimeRanges.splice(index, 1);
      console.log('newTimeRanges', newTimeRanges)
    }
    const overlap = checkOverlappingTimeRanges(newTimeRanges);
    if (overlap) {
      setErrorMessage('Time ranges cannot overlap');
      return;
    } else {
      setErrorMessage(undefined);
    }
    syncTimeRanges(newTimeRanges);
  };

  const handleAddTimeRange = (): void => {
    const newTimeRanges = [...timerangeWithIds];
    newTimeRanges.push({
      id: uuid(),
      startTime: {
        hour: newTimeRanges.at(-1)?.endTime.hour ?? 0,
        minute: newTimeRanges.at(-1)?.endTime.minute ?? 0
      },
      endTime: {
        hour: 23,
        minute: 45
      }
    });
    syncTimeRanges(newTimeRanges);
  };

  return (
    <Grid container mb={2}>
      <Grid item xs={12} sm={4} flexDirection={'column'} display={'flex'} alignItems={'flex-start'} gap={1}>
        <Typography>{day}</Typography>
        {/* <Button onClick={handleClearAvailability} sx={{ py: 0 }} color='secondary'>
          <Typography fontSize={12} leftIcon={<CopyIcon fontSize='inherit' />} color={'secondary'}>Copy Timeslots</Typography>
        </Button> */}
        <Button onClick={handleClearAvailability} sx={{ py: 0 }} color='secondary'>
          <Typography fontSize={12} leftIcon={<ClearIcon fontSize='inherit' />} color={'secondary'}>Clear Timeslots</Typography>
        </Button>
      </Grid>
      <Grid xs={12} item sm={8} flexDirection={'column'} display={'flex'} alignItems={'flex-start'} gap={1}>
        {timerangeWithIds.length === 0 && (
          <Interval onSetTimeRange={(timeRange) => {
            handleSetTimeRangeOnInterval(0, timeRange
              ? {
                  id: uuid(),
                  startTime: timeRange.startTime,
                  endTime: timeRange.endTime
                }
              : undefined
            );
          }} />
        )}
        {timerangeWithIds.map((currentTimeRange, index) => (
          <Interval key={currentTimeRange.id} timeRange={currentTimeRange} onSetTimeRange={(newRange) => {
            if (!newRange) {
              handleSetTimeRangeOnInterval(index);
              return
            }
            handleSetTimeRangeOnInterval(index, {
              id: currentTimeRange.id,
              startTime: newRange.startTime,
              endTime: newRange.endTime
            })
          }}/>
        ))}
        <Box>
          {errorMessage && (
            <Typography color={'error'}>{errorMessage}</Typography>
          )}
          {timerangeWithIds.length > 0 && (
            <Button fullWidth onClick={handleAddTimeRange} sx={{ py: 0 }} color='secondary'>
              <Typography fontSize={12} leftIcon={<AddIcon fontSize='inherit' />} color={'secondary'}>Add Another Interval</Typography>
            </Button>
          )}
        </Box>
      </Grid>
    </Grid>
  );
};
